import React from 'react';
import { twMerge } from 'tailwind-merge';

export interface CardProps {
  /** The content to render inside the card */
  children: React.ReactNode;
  /** Optional custom className for styling */
  className?: string;
  /** Optional click handler */
  onClick?: () => void;
  /** Whether the card is loading */
  isLoading?: boolean;
  /** Loading skeleton content */
  loadingContent?: React.ReactNode;
  /** Whether to show hover effects */
  hoverable?: boolean;
  /** Card variant for different styling */
  variant?: 'default' | 'elevated' | 'outlined';
  /** Card size */
  size?: 'sm' | 'md' | 'lg';
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  onClick,
  isLoading = false,
  loadingContent,
  hoverable = true,
  variant = 'default',
  size = 'md',
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return 'border border-container-border';
      case 'outlined':
        return 'border border-container-border';
      case 'default':
      default:
        return 'border border-container-border';
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'p-4';
      case 'lg':
        return 'p-8';
      case 'md':
      default:
        return 'p-6';
    }
  };

  const baseStyles = twMerge(
    'group relative overflow-hidden rounded-card bg-white dark:bg-gray-900 transition-all duration-200',
    getVariantStyles(),
    getSizeStyles(),
    hoverable && 'hover:shadow-md',
    onClick && 'cursor-pointer',
    className,
  );

  if (isLoading) {
    return (
      <div className={baseStyles}>
        {loadingContent || (
          <div className="animate-pulse">
            <div className="mb-4 h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="space-y-3">
              <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={baseStyles} onClick={onClick}>
      {children}
    </div>
  );
};

// Card Header component
export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className,
}) => {
  return <div className={twMerge('mb-4', className)}>{children}</div>;
};

// Card Content component
export interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({
  children,
  className,
}) => {
  return <div className={twMerge('', className)}>{children}</div>;
};

// Card Footer component
export interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className,
}) => {
  return <div className={twMerge('mt-4', className)}>{children}</div>;
};

// Card Title component
export interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const CardTitle: React.FC<CardTitleProps> = ({
  children,
  className,
  as: Component = 'h3',
}) => {
  return (
    <Component
      className={twMerge(
        'text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100',
        className,
      )}
    >
      {children}
    </Component>
  );
};

// Card Subtitle component
export interface CardSubtitleProps {
  children: React.ReactNode;
  className?: string;
}

export const CardSubtitle: React.FC<CardSubtitleProps> = ({
  children,
  className,
}) => {
  return (
    <p
      className={twMerge(
        'mt-1 text-sm leading-relaxed font-normal text-gray-600 dark:text-gray-400',
        className,
      )}
    >
      {children}
    </p>
  );
};
